import type { LOCATION_SEARCH_TYPE } from '@/types'
import CONFIG from 'isomorphic-config'
import debounce from 'lodash/debounce'
import { useCallback, useEffect, useRef, useState } from 'react'
import { top20Filters } from './constants'
import { fireFilterBarGA4Event } from './FilterBar/ga4'
import type { TypeAheadConfigEndpoint } from './types'
import { cityIdExistsInTop20Filters } from './utils'

type UseTypeAheadOptions = {
  searchValue: string | null
  searchKey: string
  showHotelTypeaheadFilters?: boolean
  onLoaded?: () => void
  typeAheadEndpoint: TypeAheadConfigEndpoint
  debounceTime?: number
}

type TypeAheadState = {
  loading: boolean
  error: unknown
  items: LOCATION_SEARCH_TYPE[]
  city?: string
  duration?: number
  source?: string
}

function isAbortError(error: unknown) {
  return typeof error === 'object' && error !== null && 'name' in error && error.name === 'AbortError'
}

function useTypeAhead({
  searchValue,
  searchKey,
  showHotelTypeaheadFilters = false,
  onLoaded = () => null,
  typeAheadEndpoint,
  debounceTime = 100,
}: UseTypeAheadOptions) {
  const [state, setState] = useState<TypeAheadState>({
    loading: false,
    error: null,
    items: [],
  })

  const prevSearchValueRef = useRef<string | null>(null)
  const onLoadedRef = useRef(onLoaded)
  useEffect(() => {
    onLoadedRef.current = onLoaded
  }, [onLoaded])

  const mountedRef = useRef(false)
  useEffect(() => {
    mountedRef.current = true
    return () => {
      mountedRef.current = false
    }
  }, [])

  const abortControllerRef = useRef<AbortController | null>(null)

  const abortCurrentRequest = useCallback(() => {
    abortControllerRef.current?.abort()
    abortControllerRef.current = null
  }, [])

  const fetchTypeaheadData = useCallback(
    (query: string) => {
      abortCurrentRequest()
      const controller = new AbortController()
      abortControllerRef.current = controller

      const locationConfig = CONFIG.client.typeahead.location
      const isRCSearchEndPoint = typeAheadEndpoint === 'cars'
      const parsedURLSuffix = isRCSearchEndPoint ? '' : locationConfig.suffix[typeAheadEndpoint]
      const parseURLSuffixQS = isRCSearchEndPoint ? locationConfig.querystring.cars : ''
      const url = `${locationConfig.endpoint[typeAheadEndpoint]}${query}${parsedURLSuffix}${parseURLSuffixQS}`

      setState((prev) => ({ ...prev, loading: true }))

      fetch(url, { signal: controller.signal })
        .then((res) => res.json())
        .then(
          ({
            searchItems = [],
            duration,
            source,
          }: {
            searchItems: LOCATION_SEARCH_TYPE[]
            duration: number
            source: string
          }) => {
            if (!mountedRef.current || abortControllerRef.current !== controller || controller.signal.aborted) return
            setState((prev) => ({
              ...prev,
              loading: false,
              items: searchItems,
              duration,
              source,
            }))
            onLoadedRef.current()

            const cityFilters =
              showHotelTypeaheadFilters &&
              searchItems.length > 0 &&
              cityIdExistsInTop20Filters(searchItems[0].cityID)
                ? top20Filters[searchItems[0].cityID]
                : []

            if (showHotelTypeaheadFilters && cityFilters.length > 0) {
              fireFilterBarGA4Event()
            }
          },
        )
        .catch((error) => {
          if (isAbortError(error)) return
          if (!mountedRef.current || abortControllerRef.current !== controller || controller.signal.aborted) return
          setState((prev) => ({ ...prev, loading: false, error }))
          onLoadedRef.current()
        })
    },
    [abortCurrentRequest, typeAheadEndpoint, showHotelTypeaheadFilters],
  )
  const debouncedFetchRef = useRef<ReturnType<typeof debounce> | null>(null)

  // Initialize or update the debounced fetch function when dependencies change
  useEffect(() => {
    if (debouncedFetchRef.current) {
      debouncedFetchRef.current.cancel()
    }

    debouncedFetchRef.current = debounce((query: string) => {
      fetchTypeaheadData(query)
    }, debounceTime)

    return () => {
      if (debouncedFetchRef.current) {
        debouncedFetchRef.current.cancel()
      }
      abortCurrentRequest()
    }
  }, [abortCurrentRequest, fetchTypeaheadData, debounceTime])

  useEffect(() => {
    if (prevSearchValueRef.current !== searchValue) {
      prevSearchValueRef.current = searchValue

      if (searchValue && searchValue.trim()) {
        abortCurrentRequest()
        if (debouncedFetchRef.current) {
          debouncedFetchRef.current(searchValue)
        }
      } else {
        abortCurrentRequest()
        setState({ loading: false, error: null, items: [] })
      }
    }
  }, [abortCurrentRequest, searchValue, searchKey])

  const search = useCallback(
    (value: string) => {
      if (value.trim() && debouncedFetchRef.current) {
        abortCurrentRequest()
        debouncedFetchRef.current(value)
      }
    },
    [abortCurrentRequest],
  )

  return {
    ...state,
    search,
  }
}

export default useTypeAhead
